import { useQuery } from '@tanstack/react-query';
import { UzInput } from '@utazzo/ui';
import { Check, Loader2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import type { DestinationListItem } from '@utazzo/shared';

import { getDestination, listDestinations } from '@/features/destinations/destinations.api';

/**
 * Ordered multi-select over the Destination master for CMS sections.
 *
 * Selected ids render as removable chips (chip order == stored display
 * order; new picks append at the end). Below the chips sits a
 * Domestic / International scope filter + search box over a scrollable
 * pick-list. Rows come from the standard /destinations list endpoint
 * (active rows, active countries only) so the picker always mirrors the
 * master.
 */

type ScopeFilter = 'all' | 'domestic' | 'international';

const SCOPES: ReadonlyArray<{ key: ScopeFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'domestic', label: 'Domestic' },
  { key: 'international', label: 'International' },
];

const STALE = 5 * 60_000;

/** "Goa — Goa, India" style label, mirroring the lead-form picker. */
const rowLabel = (d: DestinationListItem): string => {
  const where = [d.city?.name, d.state?.name, d.country?.name].filter(Boolean).join(', ');
  const suffix = where || d.region?.name || '';
  return suffix ? `${d.name} - ${suffix}` : d.name;
};

interface DestinationMultiSelectProps {
  label: string;
  /** Ordered destination master ids as stored on the section. */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Only list destinations that have at least one live MICE package. */
  miceOnly?: boolean;
}

export const DestinationMultiSelect = ({
  label,
  value,
  onChange,
  disabled,
  miceOnly,
}: DestinationMultiSelectProps): React.JSX.Element => {
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [search, setSearch] = useState('');
  const trimmed = search.trim();

  const listQuery = useQuery({
    queryKey: ['destinations', 'cms-multi-picker', scope, trimmed, miceOnly === true],
    queryFn: () =>
      listDestinations({
        page: 1,
        pageSize: 200,
        sortBy: 'sortOrder',
        sortDir: 'asc',
        isActive: true,
        onlyActiveCountries: true,
        ...(miceOnly ? { hasMicePackages: true } : {}),
        ...(scope === 'all' ? {} : { isInternational: scope === 'international' }),
        ...(trimmed ? { search: trimmed } : {}),
      }),
    staleTime: STALE,
  });
  const rows = useMemo(() => listQuery.data?.data ?? [], [listQuery.data]);

  // Chips must keep their names even when the row falls out of the current
  // scope/search result set, so every row we ever see feeds a label cache.
  const [labelCache, setLabelCache] = useState<Record<string, string>>({});
  useEffect(() => {
    if (rows.length === 0) return;
    setLabelCache((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const d of rows) {
        const l = rowLabel(d);
        if (next[d.id] !== l) {
          next[d.id] = l;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows]);

  // Saved ids the cache hasn't seen yet (deep links, >200th row, etc.)
  // resolve one-off via the detail endpoint; failures fall back to the id.
  const missingIds = useMemo(() => value.filter((id) => !(id in labelCache)), [value, labelCache]);
  const missingKey = useMemo(() => [...missingIds].sort().join(','), [missingIds]);
  const detailQuery = useQuery({
    queryKey: ['destinations', 'cms-multi-picker-labels', missingKey],
    enabled: missingIds.length > 0,
    queryFn: () =>
      Promise.all(
        missingIds.map(async (id) => {
          try {
            return await getDestination(id);
          } catch {
            return null;
          }
        }),
      ),
    staleTime: STALE,
  });
  useEffect(() => {
    const found = (detailQuery.data ?? []).filter(Boolean) as DestinationListItem[];
    if (found.length === 0) return;
    setLabelCache((prev) => {
      const next = { ...prev };
      for (const d of found) next[d.id] = rowLabel(d);
      return next;
    });
  }, [detailQuery.data]);

  const selected = useMemo(() => new Set(value), [value]);
  const toggle = (id: string): void => {
    if (disabled) return;
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>

      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {value.map((id, index) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full border border-(--border) bg-(--bg-subtle) py-0.5 pr-1 pl-2 text-xs text-(--fg)"
            >
              <span className="text-[10px] font-semibold text-(--fg-faint)">{index + 1}.</span>
              {labelCache[id] ?? id}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onChange(value.filter((v) => v !== id))}
                  className="rounded-full p-0.5 text-(--fg-faint) hover:text-(--fg)"
                  aria-label={`Remove ${labelCache[id] ?? id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="rounded-md border border-(--border)">
        <div className="flex items-center gap-1 border-b border-(--border) p-2">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setScope(s.key)}
              disabled={disabled}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                scope === s.key
                  ? 'bg-(--fg) text-(--bg)'
                  : 'bg-(--bg-subtle) text-(--fg-muted) hover:text-(--fg)'
              }`}
            >
              {s.label}
            </button>
          ))}
          <div className="ml-auto w-44">
            <UzInput
              value={search}
              placeholder="Search destinations…"
              disabled={disabled}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="max-h-56 overflow-y-auto p-1">
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-(--fg-faint)">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading destinations…
            </div>
          ) : listQuery.isError ? (
            <p className="py-6 text-center text-xs text-rose-600">
              Failed to load destinations. Try again.
            </p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-xs text-(--fg-faint)">
              {miceOnly
                ? 'No destinations with MICE packages yet - mark packages as MICE first.'
                : 'No destinations found.'}
            </p>
          ) : (
            rows.map((d) => {
              const isPicked = selected.has(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggle(d.id)}
                  disabled={disabled}
                  className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-(--bg-subtle) ${
                    isPicked ? 'text-(--fg)' : 'text-(--fg-muted)'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      isPicked ? 'border-(--fg) bg-(--fg) text-(--bg)' : 'border-(--border)'
                    }`}
                  >
                    {isPicked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{rowLabel(d)}</span>
                  <span className="shrink-0 rounded bg-(--bg-subtle) px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-(--fg-faint) uppercase">
                    {d.isInternational ? 'International' : 'Domestic'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <p className="mt-1 text-[11px] text-(--fg-faint)">
        Picks append to the end of the list - chip order above is the display order on the page.
      </p>
    </div>
  );
};
